import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnChanges, OnInit, Signal, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PrivateLesson } from '@lx/shared/data-access/products';
import { Amount } from '@lx/shared/data-access/types';
import { GoogleAnalyticsService } from '@lx/shared/google-analytics';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { Observable } from 'rxjs';
import { GoogleAnalyticsEventEnum } from '../../google-analytics';
import { QTLiteStoreSignalsAdapterService } from '../../services/qt-lite-store-signals-adapter.service';
import { QuoteOutputProduct, QuoteStore, QuoteToolLiteStore } from '../../store';
import { OperationKey } from '../../constants';
import { CardContainerComponent } from '@lx/quote-tool/lite/shared/ui/components';

@Component({
  standalone: true,
  selector: 'lx-private-lesson',
  templateUrl: './private-lesson.component.html',
  styleUrls: ['./private-lesson.component.scss'],
  imports: [CommonModule, CardContainerComponent],
})
export class PrivateLessonComponent implements OnInit, OnChanges {
  @Input() privateLesson: PrivateLesson;

  price: Amount[] | undefined;
  endPrice: Amount[] | undefined;
  discount: string | undefined;
  units = 0;
  duration$: Observable<number> | undefined;
  currency: Signal<string | undefined>;
  protected loading: Signal<boolean | undefined>;

  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly qtLiteStoreSignalsAdapterService = inject(QTLiteStoreSignalsAdapterService);
  private readonly quoteStore = inject(QuoteStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);
  private readonly loadingService = inject(LoadingService);

  ngOnChanges({ privateLesson }: SimpleChanges) {
    if (privateLesson?.currentValue) {
      const totalPriceFromExtrasInsurance = privateLesson.currentValue.multiCurrencyPrice;
      this.endPrice = totalPriceFromExtrasInsurance;
      this.price = totalPriceFromExtrasInsurance;
    }
  }

  ngOnInit(): void {
    this.duration$ = this.quoteToolLiteStore.selectedDuration$;
    this.quoteStore.privateLessons$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((quotedPrivateLessons: QuoteOutputProduct) => {
      this.setPropertiesFromQuotedPrivateLessons(quotedPrivateLessons);
    });

    this.currency = this.qtLiteStoreSignalsAdapterService.selectedCurrency;
    this.loading = this.loadingService.areAllOperationsLoading([OperationKey.prices, OperationKey.extras]);
  }

  setWeeksChanges(weeks: number) {
    this.units = weeks;
    this.quoteToolLiteStore.updateState({ selectedPrivateLesson: { code: this.privateLesson.code, units: weeks } });
    this.googleAnalyticsService.trackClickEvent(GoogleAnalyticsEventEnum.QuotePrivateLessonsSelect);
  }

  private setPropertiesFromQuotedPrivateLessons(quotedInsurance: QuoteOutputProduct): void {
    this.endPrice = quotedInsurance?.endPrice || this.endPrice;
    this.price = quotedInsurance?.price || this.endPrice;
    this.discount = quotedInsurance?.discountTotalAsPercentage;
    this.units = quotedInsurance?.units || 0;
  }
}
